/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener('variant:changed', function (event) {
  LalUtils.handleVariantChanged(event)
  LalUtils.setupBackInStock(event)
})

document.addEventListener('variant:changed:sort-init', function (event) {
  LalUtils.handleVariantChanged(event)
  LalUtils.setupBackInStock(event)
})

document.addEventListener('lal:global-handler', function (event) {
  try {
    if (event.detail.type === 'geo-filter-products') {
      //first get ip location data
      const geoDataQuery =
        LALGlobal.store.data[LALGlobal.store.storeKeys.GEO_LOCATION_DATA]

      if (geoDataQuery.state == 'error') return

      if (geoDataQuery.state == 'empty') {
        //if not available yet, trigger fetching of event and then re-dispatch this event
        const attemptNum = (event.detail.attemptNum || 1) + 1

        if (attemptNum <= 3) {
          setTimeout(
            () =>
              document.dispatchEvent(
                new CustomEvent('lal:global-handler', {
                  detail: {
                    type: 'geo-filter-products',
                    attemptNum,
                  },
                })
              ),
            200
          )
        }
      }

      if (geoDataQuery.state == 'loaded') {
        const geoData = geoDataQuery.data

        if (geoData.continent_code != 'NA') {
          //hide large products outside of NA
          // const productItems = document.querySelectorAll(
          //   'product-item[is-big-product]'
          // )

          // productItems.forEach((element) => {
          //   element.style.display = 'none'
          // })

          // const addToCarts = document.querySelectorAll('button[is-big-product]')

          // addToCarts.forEach((element) => {
          //   element.disabled = true
          //   if (element.id == 'AddToCart') {
          //     const notAvailId = 'notAvailableText'
          //     notAvailEle = document.getElementById(notAvailId)

          //     if (!notAvailEle) {
          //       const notAvailableInRegion = document.createElement('p')
          //       notAvailableInRegion.id = notAvailId
          //       notAvailableInRegion.textContent =
          //         'This item is not available in your region.'
          //       if (element && element.parentNode) {
          //         element.parentNode.insertBefore(
          //           notAvailableInRegion,
          //           element.nextSibling
          //         )
          //       }
          //     }
          //   }
          // })

          if (geoData.country_code == 'IT') {
            //add note if in italy
            var italyNote = document.querySelectorAll('[italy-note]')

            if (italyNote.length == 0) {
              const italyNoteHolders = document.querySelectorAll(
                'div[italy-note-parent]'
              )
              var italyInfo = `<p italy-note class="text--subdued text--xsmall italy-note" style="padding: .5rem 0;">Hey! It looks like you're in Italy. Due to shipping constraints, there is a maximum of 5 items allowed per order. </p>`
              italyNoteHolders.forEach((element) => {
                element.innerHTML += italyInfo
              })
            }
          }
        }
      }

      if (LALGlobal.settings.blnBlackFridayMode) {
        enforceBlackFridayLimits()
      }
    }
  } catch (err) {
    console.error('an error occured in event handler lal:global-handler ', err)
  }
})

function enforceBlackFridayLimits() {
  try {
    //first remove all restrictions
    const productItems = document.querySelectorAll('product-item.bf-preventAdd')
    productItems.forEach((item) => {
      item.classList.remove('bf-preventAdd')
    })

    const checkTweezerLimit = document.querySelector('[data-bf-limit-tweezer]')
    const checkLiquidLimit = document.querySelectorAll('[data-bf-limit-liquid]')

    // console.log(checkTweezerLimit);
    // console.log(checkLiquidLimit);

    if (checkTweezerLimit) {
      //hide / disable ANY tweezer product-item form

      const productItems = document.querySelectorAll(
        'product-item[data-bf-limit-product-type="tweezer"]'
      )

      //this prevents PLP, you may also like, and cart pop out recommendations
      productItems.forEach((item) => {
        item.classList.add('bf-preventAdd')
      })

      //PDP page
      const addToCart = document.getElementById('AddToCart')
      if (addToCart) {
        if (addToCart.dataset.productType == 'tweezer') {
          addToCart.disabled = true
        }
        //liquid and tweezer shipping notice text, make bold and red
        const bfLimitNote = document.getElementById('bfLimitNoteTweezer')
        if (bfLimitNote) {
          bfLimitNote.style.fontWeight = 500
          bfLimitNote.style.color = '#d31414'
        }
      }
    }

    if (checkLiquidLimit.length > 0) {
      checkLiquidLimit.forEach((item) => {
        liquidIdToLimit = item.dataset.bfLimitId
        const productItems = document.querySelectorAll(
          `product-item[data-bf-limit-product-type="liquid"][data-bf-limit-product-id="${liquidIdToLimit}"]`
        )

        //this prevents PLP, you may also like, and cart pop out recommendations
        productItems.forEach((productItem) => {
          productItem.classList.add('bf-preventAdd')
        })
        const addToCart = document.getElementById('AddToCart')
        if (addToCart) {
          if (
            addToCart.dataset.productType == 'liquid' &&
            addToCart.dataset.productId == liquidIdToLimit
          ) {
            addToCart.disabled = true

            //liquid and tweezer shipping notice text, make bold and red
            const bfLimitNote = document.getElementById('bfLimitNoteLiquid')
            if (bfLimitNote) {
              bfLimitNote.style.fontWeight = 500
              bfLimitNote.style.color = '#d31414'
            }
          }
        }
      })
    }
  } catch (err) {
    console.error('error during bf limit:', err)
  }
}

document.addEventListener(
  'lal:fetch:geo-location-data',
  async function (event) {
    const queryKey = LALGlobal.store.storeKeys.GEO_LOCATION_DATA
    try {
      let rawGeoData = localStorage.getItem(LalUtils.IPDATA)
      let geoData

      if (!rawGeoData) {
        //fetch data

        LALGlobal.store.fetching[queryKey]
        const url = `http://api.ipstack.com/check?access_key=${LalUtils.IPSTACKAPIKEY}`
        const result = await fetch(url)

        if (!result.ok) throw new Error('api ipstack request was not ok')

        geoData = await result.json()

        localStorage.setItem(LalUtils.IPDATA, JSON.stringify(geoData))
      } else {
        geoData = JSON.parse(rawGeoData)
      }

      if (!geoData) throw new Error('no geoData avaiable')

      LALGlobal.store.data[queryKey] = { state: 'loaded', data: geoData }

      //calling this here will hide collection items, recommendations on PDP
      document.dispatchEvent(
        new CustomEvent('lal:global-handler', {
          detail: { type: 'geo-filter-products' },
        })
      )
    } catch (err) {
      console.error('Unable to fetch IP data', err)
      LALGlobal.store.data[queryKey] = {
        state: 'error',
        errorMessage: err,
      }
    } finally {
      //clean up queryKey in fetching list
      LALGlobal.store.removeFetching[queryKey]
    }
  }
)

document.addEventListener('DOMContentLoaded', async function (event) {
  document.dispatchEvent(new CustomEvent('lal:fetch:geo-location-data'))

  //setup back in stock

  const backInStock = document.querySelector('#back-in-stock')
  if (backInStock) {
    backInStock.addEventListener('submit', async function (e) {
      e.preventDefault()

      try {
        const submitButtons = Array.from(this.elements).filter(
          (button) => button.type === 'submit'
        )
        submitButtons.forEach((submitButton) => {
          submitButton.setAttribute('disabled', 'disabled')
          submitButton.setAttribute('aria-busy', 'true')
        })

        const subscribe = document.getElementById('subscribe_for_newsletter')

        var formData = new FormData(this)
        formData.append('a', 'Rq8nbX')
        formData.append('g', 'TaHXsU')
        formData.append('platform', 'shopify')

        if (subscribe.checked) {
          formData.append('subscribe_for_newletter', 'true')
        }

        const result = await fetch(
          'https://a.klaviyo.com/onsite/components/back-in-stock/subscribe',
          { method: 'POST', body: formData }
        )

        submitButtons.forEach((submitButton) => {
          submitButton.removeAttribute('disabled')
          submitButton.removeAttribute('aria-busy')
        })

        if (!result.ok) {
          throw new Error('Network response was not ok' + result.statusText)
        }

        responseJson = await result.json()

        document.getElementById('outofstock-completed_message').style.display =
          'block'

        // Your custom JavaScript logic here
      } catch (err) {
        console.log('Unable to subscribe to out of stock, error: ', err)
      }
    })
  }
})

class LALGlobal {
  static settings = { APPLYFREEGIFT: false, blnBlackFridayMode: false }
  static store = {
    storeKeys: {
      GEO_LOCATION_DATA: 'geo-location-data',
    },
    addFetching: function (key) {
      if (this.fetching.includes(key)) return

      this.fetching.push(key)
    },
    removeFetching: function (key) {
      const index = this.fetching.indexOf(key)
      if (index !== -1) {
        this.fetching.splice(index, 1)
      }
    },
    fetching: [],
    data: { 'geo-location-data': { state: 'empty' } },
  }
}

class LalUtils {
  static IPDATA = 'IP_DATA'
  static IPSTACKAPIKEY = '110ec80020fb410985da2b7f54575309'
  static SORT_ORDER = {
    liquid: '100-10',
    accessory: '100-20',
    aftercare: '100-30',
    tweezer: '100-40',
    lashes: {
      easyfans: {
        standard: '200-10',
        camellia: '200-30',
        vneck: '250',
        hintofcolour: '200-20',
      },
      classic: '280-10',
      volume: {
        black: '270-10',
        rainbow: '200-60',
        colour: '200-50',
        ultradark: '200-40',
        textured: '900-40',
      },
      flat: '260-20',
      colouredmidstem: '230',
      profans: {
        shortstem: '280-50', //$5 trays are here
        trays: {
          XXLECO: '10',
          small: '20',
          attachedmid: '30',
          eco: '40',
          large: '44',
          XL: '45',
          attachedbase: '50',
        },
        longstem: '200-80',
        ultradark: '200-85',
        ultradarkshortstem: '300-10',
        midstem: '300-20',
        ultradarklongstem: '300-30',
      },
      wrapid: '200-90',
      satinspikes: '200-95',
      'premade spikes': '240',
    },
    vneck: '250',
    'citizen lash': '900-50', //citizens already work on the invoice in their own area, this value here doesn't matter
  }

  static setupBackInStock(event) {
    try {
      const variant = event.detail.variant

      if (variant.available) {
        document.getElementById('outofstock-notify-button').style.display =
          'none'
        return
      } else {
        document.getElementById('outofstock-notify-button').style.display =
          'block'
      }
      const variantId = document.getElementById('outofstock-variant-id')
      const title = document.getElementById('outofstock-title')
      const subTitle = document.getElementById('outofstock-subtitle')

      variantId.value = event.detail.variant.id

      //Product Title - 4D / D / 10
      const titleArr = event.detail.variant.name.split(' - ')

      title.textContent = titleArr[0]

      if (titleArr.length > 1) {
        subTitle.textContent = titleArr[1]
      }
    } catch (err) {
      console.log('unable to update backinstock in variant:changed', err)
    }
  }

  static handleVariantChanged(event) {
    let eventData = event.detail // Gives you access to the whole variant details

    var rawProductInfo = document.getElementById('ProductJson')
    if (!rawProductInfo) return

    var productInfo = JSON.parse(rawProductInfo.innerHTML)

    if (!productInfo) {
      console.warn('handleVariantChanged: no productInfo setup ')
      return
    }

    if (eventData.variant) {
      //mega product variant changed. Need to Update invoice order
      LalUtils.setInvoiceOrdering(productInfo, eventData.variant)

      //
      //
      //
    } else {
      console.warn(
        'no productInfo or variant data provided to handleVariantChanged'
      )
    }
  }

  static cleanOrdering(strCustomOrder) {
    var cleansed = strCustomOrder.replace(/\s+/g, '') //remove spaces
    cleansed = cleansed.replace(/[^0-9a-zA-Z-]/g, '') //remove spaces

    return cleansed.toUpperCase()
  }

  static applyOrdering(strCustomOrder) {
    console.log('applyOrdering of ', strCustomOrder)
    try {
      document.getElementById('invoiceorder').value =
        strCustomOrder.toUpperCase()
    } catch (err) {
      console.log(
        'unable to set invoice sort order, element invoiceorder not found'
      )
    }
  }

  static genBlowoutSort(variant) {
    //variants are setup with DIMENSION / CURL / LENGTH
    var dimension = variant.option1.padStart(3, '0')

    var strReturn =
      '-' +
      dimension +
      '-' +
      variant.option2 +
      '-' +
      variant.option3.padStart(2, '0')
    return strReturn
  }

  genMidStemBlowoutSort(variant) {
    /*variants are setup with 
    option1 = Dimension
    option2 = Curl
    option3 = Length
    option4 = Diameter
    DIMENSION / CURL / LENGTH

    */

    var dimension = variant.option1.padStart(3, '0')
    var paddedLength = variant.option3.padStart(2, '0')

    var strReturn = `${dimension}-${variant.option2}-${paddedLength}-${variant.option4}`

    return strReturn
  }

  static genNaturalSort(variant) {
    return `-${variant.option1.padStart(3, '0')}-${variant.option2.padStart(
      3,
      '0'
    )}-${variant.option3.padStart(3, '0')}`
  }
  static genNaturalProfanSort(variant) {
    //NEW
    //option 0: FAN TYPE - determined by SKU: VL, VLMS, VLLS, or VLUD
    //option 1: DIMENSION
    //option 2: CURL
    //option 3: LENGTH
    //option 4: TRAY SIZE
    var dimension = variant.name.substr(0, 3).trim().padStart(3, '0') //gets for ex "4D" from the variant name "4D PRO FAN"
    var strReturn =
      '-' +
      dimension +
      '-' +
      variant.option2 + //CURL
      '-' +
      variant.option3.padStart(2, '0') //LENGTH
    return strReturn
  }

  static getNumericTraySize(variantTitle) {
    var order = 999

    if (variantTitle.includes('/ XXL ECO')) order = '10'
    else if (variantTitle.includes('/ SMALL')) order = '20'
    else if (variantTitle.includes(' /ECO')) order = '40'
    else if (variantTitle.includes('/ LARGE')) order = '44'
    else if (variantTitle.includes('/ XL')) order = '45'

    return '-' + order
  }

  static getFanTypeSort(sku) {
    var profansorting = LalUtils.SORT_ORDER.lashes.profans
    var fanOrder
    if (sku) {
      if (sku.startsWith('VL-')) fanOrder = profansorting.shortstem
      else if (sku.startsWith('VLMS-')) fanOrder = profansorting.midstem
      else if (sku.startsWith('VLLS-')) fanOrder = profansorting.longstem
      else if (sku.startsWith('VLUD-'))
        fanOrder = profansorting.ultradarkshortstem
      else if (sku.startsWith('VLLSUD-'))
        fanOrder = profansorting.ultradarklongstem
    }

    if (!fanOrder) fanOrder = '200-99'

    return fanOrder
  }

  static getSortForFanAttachment(variant) {
    var fanAttachmentOrder = ''
    //check option3 and option 4, option3 is for standalone products, option4 is for super duper
    var blnMiddleSticky =
      variant.option4.includes('Middle') || variant.option3.includes('Middle')
    var blnBaseSticky =
      variant.option4.includes('Base') || variant.option3.includes('Base')

    //
    if (blnBaseSticky)
      fanAttachmentOrder =
        '-' + LalUtils.SORT_ORDER.lashes.profans.trays.attachedbase
    else if (blnMiddleSticky)
      fanAttachmentOrder =
        '-' + LalUtils.SORT_ORDER.lashes.profans.trays.attachedmid

    return fanAttachmentOrder
  }

  static getSortOrderForNonVariantProductsAndQuickAdd(event) {
    try {
      var sortData = event.target.dataset || {}

      if (!(sortData.sortType && sortData.sortId && sortData.sortTitle)) {
        console.log('Unable to set sort data with ', sortData)
      }

      var sortOrder

      if (sortData.sortTitle.toUpperCase().includes('V NECK')) {
        sortOrder = LalUtils.SORT_ORDER['vneck']
      } else if (sortData.sortTitle.toUpperCase().includes('DIY')) {
        sortOrder = LalUtils.SORT_ORDER['citizen lash']
      } else {
        sortOrder = LalUtils.SORT_ORDER[sortData.sortType] || '600'
      }

      sortOrder +=
        '-' + sortData.sortTitle.replaceAll('-', '').replaceAll(' ', '')
      sortOrder += '-' + sortData.sortId

      sortOrder = LalUtils.cleanOrdering(sortOrder)

      return sortOrder
    } catch (err) {
      console.log('error in setSortOrderForNonVariantProductsAndQuickAdd ', err)
    }
  }

  static setInvoiceOrdering(productInfo, variant) {
    var mapping = LalUtils.SORT_ORDER.lashes
    var sortOrder = ''

    try {
      if (productInfo.type == 'classic') {
        sortOrder = mapping.classic + LalUtils.genNaturalSort(variant)
      } else if (productInfo.type == 'volume') {
        if (productInfo.tags.includes('volume black')) {
          if (variant.option0.toUpperCase() == 'STANDARD') {
            sortOrder = mapping.volume.black + LalUtils.genNaturalSort(variant)
          } else if (variant.option0.toUpperCase() == 'ULTRA DARK') {
            sortOrder =
              mapping.volume.ultradark + LalUtils.genNaturalSort(variant)
          } else {
            sortOrder =
              mapping.volume.textured + LalUtils.genNaturalSort(variant)
          }
        } else if (productInfo.tags.includes('volume rainbow')) {
          sortOrder = mapping.volume.rainbow + LalUtils.genNaturalSort(variant)
        } else if (productInfo.tags.includes('volume color')) {
          //volume colour sorts with 1/3/2
          sortOrder = `${mapping.volume.colour}-${variant.option1}-${variant.option3}-${variant.option2}`
        } else sortOrder = '200-10-999' + LalUtils.genNaturalSort(variant)
      } else if (productInfo.type == 'flat') {
        sortOrder = mapping.flat + LalUtils.genNaturalSort(variant)
      } else if (productInfo.type == 'easy fans') {
        if (variant.sku.startsWith('VN-')) {
          sortOrder = `${mapping.easyfans.vneck}-${variant.sku}`
        } else if (variant.option0.toUpperCase() == 'STANDARD') {
          sortOrder =
            mapping.easyfans.standard + LalUtils.genNaturalSort(variant)
        } else if (variant.option0.toUpperCase() == 'TEXTURED') {
          sortOrder =
            mapping.easyfans.camellia + LalUtils.genNaturalSort(variant)
        } else if (variant.name.includes('Hint')) {
          sortOrder =
            mapping.easyfans.hintofcolour + LalUtils.genNaturalSort(variant)
        } else {
          sortOrder =
            mapping.easyfans.standard + LalUtils.genNaturalSort(variant)
        }
      } else if (
        productInfo.type == 'pro-fan volume' &&
        productInfo.tags.includes('medium stem') &&
        productInfo.tags.includes('pro-fan colour')
      ) {
        // COLOURED MID STEM
        sortOrder = `${mapping.colouredmidstem}-${variant.option0}-${variant.option1}-${variant.option2}-${variant.option3}`
      } else if (productInfo.type == 'premade spikes') {
        sortOrder = `${mapping['premade spikes']}-${variant.sku}`
      } else if (productInfo.type == 'pro-fan volume') {
        //////START OF PRO FAN VOLUME /////
        if (
          (productInfo.tags.includes('short stem') ||
            productInfo.tags.includes('medium stem') ||
            productInfo.tags.includes('long stem')) &&
          productInfo.tags.includes('pro-fan black')
        ) {
          //short stem $5 dollar products are getting here
          sortOrder = `${LalUtils.getFanTypeSort(
            variant.sku
          )}-${variant.option1.padStart(3, '0')}-${variant.sku}`
        } else {
          //fall back if we get here.
          console.warn('fell into else statement in sort for pro-fan')
          sortOrder = '700-10-20' + variant.sku
        }
        //////END OF PRO FAN VOLUME /////
      } else {
        //otherwise we don't have sorting setup for this product, just use the SKU
        console.warn('no sorting setup for product')
        sortOrder = variant.sku
      }

      sortOrder = LalUtils.cleanOrdering(sortOrder)

      sortOrder = sortOrder + '-' + variant.id //we need this for uniqueness for the invoice pdf ordering
    } catch (err) {
      console.log('error during sort order generation,', err)
      sortOrder = variant.sku || variant.id
    }

    // console.log("apply sort order of " + sortOrder);

    //console.log("in _setInvoiceOrdering,  calling applyOrdering with sortOrder =  ",sortOrder)
    LalUtils.applyOrdering(sortOrder)
  }
}

if (LALGlobal.settings.APPLYFREEGIFT) {
  document.addEventListener('cart:updated', async function (event) {
    try {
      var cart = event.detail.cart // Get the updated content of the cart

      const FREE_GIFT_PROPERTY = '000-FREE'

      if (cart && cart.total_price >= 20000) {
        const shouldApply = !cart.items.find(
          (cartItem) =>
            cartItem.properties &&
            cartItem.properties['_invoiceorder'] == FREE_GIFT_PROPERTY
        )

        if (shouldApply) {
          const properties = { _invoiceorder: FREE_GIFT_PROPERTY }
          const INVISABOND_VARIANTID = 31452609183798 //invisabond
          var items = {
            items: [{ id: INVISABOND_VARIANTID, quantity: 1, properties }],
          }

          setTimeout(async () => {
            const freeGiftResponse = await fetch(
              `${window.themeVariables.routes.cartAddUrl}.js`,
              {
                body: JSON.stringify(items),
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
              }
            )

            await freeGiftResponse.json()

            if (freeGiftResponse.ok) {
              document.documentElement.dispatchEvent(
                new CustomEvent('cart:refresh', {
                  bubbles: true,
                })
              )
            }
          }, 300)
        }
      }
    } catch (err) {
      console.log('error adding free gift', err)
    }
  })
}
